import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Bell, AlertTriangle, Info, X, Check, CheckCheck } from "lucide-react";

const SEVERITY_COLOR = {
  info: "#1A3C5E",
  warning: "#E67E22",
  critical: "#C0392B",
};

export default function AlertsBell() {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/alerts?limit=20");
      setItems(data.items || []);
      setUnreadCount(data.unread_count || 0);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000); // toutes les minutes
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markRead = async (id) => {
    await api.post(`/alerts/${id}/read`);
    refresh();
  };

  const markAll = async () => {
    await api.post("/alerts/read-all");
    refresh();
  };

  const remove = async (id) => {
    await api.delete(`/alerts/${id}`);
    refresh();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-sm hover:bg-secondary transition-colors"
        data-testid="alerts-bell"
        title="Alertes & veille"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-full"
            style={{ background: "#C0392B", color: "white", minWidth: "18px", textAlign: "center" }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[380px] max-h-[500px] overflow-hidden rounded-sm border shadow-xl z-50"
          style={{
            background: "hsl(var(--card))",
            borderColor: "hsl(var(--border))",
          }}
          data-testid="alerts-dropdown"
        >
          <div className="flex items-center justify-between p-3 border-b"
            style={{ borderColor: "hsl(var(--border))" }}>
            <div className="font-display font-bold text-sm flex items-center gap-2">
              <Bell size={14} style={{ color: "#D4A017" }} />
              Alertes ({items.length})
            </div>
            {unreadCount > 0 && (
              <button onClick={markAll}
                className="text-[11px] font-semibold hover:underline flex items-center gap-1"
                style={{ color: "#D4A017" }}
                data-testid="alerts-mark-all-read">
                <CheckCheck size={12} /> Tout marquer lu
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-xs opacity-60">
                Aucune alerte. La veille scanne tous les projets toutes les heures.
              </div>
            ) : (
              items.map((a) => {
                const color = SEVERITY_COLOR[a.severity] || "#1A3C5E";
                const Icon = a.severity === "critical" || a.severity === "warning"
                  ? AlertTriangle : Info;
                return (
                  <div key={a.id}
                    className={`group p-3 border-b transition-colors ${a.read ? "opacity-60" : ""}`}
                    style={{
                      borderColor: "hsl(var(--border))",
                      borderLeft: `3px solid ${a.read ? "transparent" : color}`,
                      background: a.read ? "transparent" : `${color}08`,
                    }}
                    data-testid={`alert-${a.id}`}>
                    <div className="flex items-start gap-2">
                      <Icon size={14} style={{ color, marginTop: 2, flexShrink: 0 }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-xs mb-1">
                          {a.project_id ? (
                            <Link to={`/projects/${a.project_id}`}
                              onClick={() => setOpen(false)}
                              className="hover:underline">
                              {a.title}
                            </Link>
                          ) : a.title}
                        </div>
                        {a.body && <div className="text-xs opacity-80 leading-relaxed">{a.body}</div>}
                        <div className="font-mono text-[10px] opacity-50 mt-1">
                          {new Date(a.created_at).toLocaleString("fr-FR")}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!a.read && (
                          <button onClick={() => markRead(a.id)}
                            className="p-1 hover:bg-secondary rounded-sm"
                            title="Marquer comme lu" data-testid={`alert-read-${a.id}`}>
                            <Check size={12} />
                          </button>
                        )}
                        <button onClick={() => remove(a.id)}
                          className="p-1 hover:bg-secondary rounded-sm"
                          title="Supprimer" data-testid={`alert-delete-${a.id}`}>
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="p-2 border-t text-center"
            style={{ borderColor: "hsl(var(--border))" }}>
            <button onClick={async () => { await api.post("/alerts/run-veille"); refresh(); }}
              className="text-[11px] opacity-70 hover:opacity-100 hover:underline font-mono"
              data-testid="alerts-run-veille">
              Lancer la veille manuellement
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
